import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaBolt, FaCheckCircle, FaExclamationTriangle, FaExternalLinkAlt, FaSave, FaTimes, FaUnlink } from 'react-icons/fa';
import config from '../../config.js';
import { useFetchWrapper } from '../../_helpers/fetchWrapper.js';
import { useT } from '../../contexts/LocaleContext.jsx';
import { richMessage } from '../../_helpers/richMessage.jsx';
import '../../styles/BandSettings.css';
import '../../styles/partial/StreamElementsConnect.css';

const SE_ACCOUNT_URL = 'https://streamelements.com/dashboard/account#channels';

function isValidJwtShape(value) {
  return /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(value.trim());
}

/**
 * StreamElements provider card for the Overlay Integration page. Renders as a
 * self-contained card (no page header) so it can sit alongside other
 * providers; the surrounding page owns the title, back navigation and the
 * band-member guard.
 */
function StreamElementsConnect({ band }) {
  const [jwt, setJwt] = useState('');
  // The card names the channel, never any part of the token (ENG-560): the
  // status carries channel_id and no JWT.
  const [status, setStatus] = useState({ connected: false, channelId: '' });
  const [loading, setLoading] = useState(true);
  // The status request failed (ENG-516): the connection is unknown, not
  // absent, so neither badge shows and the JWT form stays hidden — a
  // connected performer would otherwise paste a new token over a working one.
  const [statusFailed, setStatusFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [message, setMessage] = useState(null);

  const fetchWrapperRef = useRef(useFetchWrapper());
  const timerRef = useRef(null);
  const t = useT();

  const bandId = band?.ID ?? band?.id;

  const loadStatus = useCallback(async () => {
    if (!bandId) return;
    try {
      const data = await fetchWrapperRef.current.get(
        `${config.apiBaseUrl}/bands/${bandId}/streamelements/status`
      );
      setStatus({
        connected: Boolean(data?.connected),
        channelId: typeof data?.channel_id === 'string' ? data.channel_id.trim() : '',
      });
      setStatusFailed(false);
    } catch {
      setStatus({ connected: false, channelId: '' });
      setStatusFailed(true);
    } finally {
      setLoading(false);
    }
  }, [bandId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const showMessage = (text, type = 'success') => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setMessage({ text, type });
    timerRef.current = setTimeout(() => setMessage(null), 5000);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const trimmed = jwt.trim();
    if (!isValidJwtShape(trimmed)) {
      showMessage(t('overlay.streamElements.errors.invalidJwt'), 'error');
      return;
    }
    setSaving(true);
    try {
      await fetchWrapperRef.current.post(
        `${config.apiBaseUrl}/bands/${bandId}/streamelements/connect`,
        { jwt: trimmed }
      );
      setJwt('');
      await loadStatus();
      showMessage(t('overlay.streamElements.success.connected'));
    } catch {
      showMessage(t('overlay.streamElements.errors.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await fetchWrapperRef.current.delete(
        `${config.apiBaseUrl}/bands/${bandId}/streamelements/disconnect`
      );
      setStatus({ connected: false, channelId: '' });
      showMessage(t('overlay.streamElements.success.disconnected'));
    } catch {
      showMessage(t('overlay.streamElements.errors.disconnectFailed'), 'error');
    } finally {
      setDisconnecting(false);
    }
  };

  // Every request below is keyed on the band id; without one there is nothing
  // to connect, so render nothing rather than a form that would POST to
  // /bands/undefined/... (the page never mounts this without a band today).
  if (!bandId) return null;

  return (
    <section className="se-connect-card" aria-labelledby="se-connect-card-title">
      <div className="se-connect-card-header">
        <FaBolt className="se-connect-logo-icon" aria-hidden="true" />
        <div className="se-connect-card-info">
          <h3 id="se-connect-card-title" className="se-connect-card-title">{t('overlay.streamElements.name')}</h3>
          <p className="se-connect-card-description">
            {t('overlay.streamElements.description')}
          </p>
        </div>
        {!loading && !statusFailed && (
          <div className="se-connect-status-badge" aria-live="polite">
            {status.connected ? (
              <span className="se-connect-badge se-connect-badge--connected">
                <FaCheckCircle aria-hidden="true" /> {t('common.status.connected')}
              </span>
            ) : (
              <span className="se-connect-badge se-connect-badge--disconnected">
                {t('common.status.notConnected')}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="se-connect-card-body">
        {message && (
          <div
            className={`message ${message.type === 'error' ? 'error-message' : 'success-message'}`}
            role={message.type === 'error' ? 'alert' : 'status'}
          >
            {message.type === 'error' ? (
              <FaExclamationTriangle className="message-icon" aria-hidden="true" />
            ) : (
              <FaCheckCircle className="message-icon" aria-hidden="true" />
            )}
            {message.text}
          </div>
        )}

        {loading ? (
          <p className="se-connect-loading" aria-live="polite">{t('overlay.status.checking')}</p>
        ) : statusFailed ? (
          <p className="message error-message" role="alert" data-testid="se-status-failed">
            <FaExclamationTriangle className="message-icon" aria-hidden="true" />
            {t('overlay.streamElements.errors.statusFailed')}
          </p>
        ) : status.connected ? (
          <div className="se-connect-actions">
            <p className="se-connect-connected-message">
              {status.channelId
                ? richMessage(t('overlay.streamElements.connectedMessage'), {
                    channel: <code className="se-connect-channel">{status.channelId}</code>,
                  })
                : t('overlay.streamElements.connectedMessageNoChannel')}
            </p>
            <button
              type="button"
              className="form-button se-connect-disconnect-btn"
              onClick={handleDisconnect}
              disabled={disconnecting}
              aria-label={t('overlay.streamElements.disconnect')}
            >
              <FaUnlink aria-hidden="true" />
              {disconnecting ? t('overlay.status.disconnecting') : t('overlay.streamElements.disconnect')}
            </button>
          </div>
        ) : (
          <>
            <div className="se-connect-instructions">
              <p>
                <a
                  href={SE_ACCOUNT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="se-connect-deeplink"
                  aria-label={t('overlay.streamElements.getTokenLinkLabel')}
                >
                  {t('overlay.streamElements.getTokenLink')}
                  <FaExternalLinkAlt className="se-connect-deeplink-icon" aria-hidden="true" />
                </a>
              </p>
              <ol className="se-connect-steps">
                <li>{t('overlay.streamElements.steps.openLink')}</li>
                <li>
                  {richMessage(t('overlay.streamElements.steps.findChannels'), {
                    channels: <strong>{t('overlay.streamElements.ui.channels')}</strong>,
                  })}
                </li>
                <li>
                  {richMessage(t('overlay.streamElements.steps.showSecret'), {
                    showSecret: <strong>{t('overlay.streamElements.ui.showSecret')}</strong>,
                  })}
                </li>
                <li>
                  {richMessage(t('overlay.streamElements.steps.paste'), {
                    save: <strong>{t('common.actions.save')}</strong>,
                  })}
                </li>
              </ol>
            </div>

            <form className="se-connect-form" onSubmit={handleSave} noValidate>
              <label className="se-connect-label" htmlFor="se-jwt-input">
                {t('overlay.streamElements.jwtLabel')}
              </label>
              <textarea
                id="se-jwt-input"
                className="se-connect-textarea"
                value={jwt}
                onChange={(e) => setJwt(e.target.value)}
                placeholder={t('overlay.streamElements.jwtPlaceholder')}
                rows={4}
                autoComplete="off"
                spellCheck={false}
                aria-label={t('overlay.streamElements.jwtInputLabel')}
              />
              <div className="se-connect-form-actions">
                <button
                  type="button"
                  className="form-button cancel-button"
                  onClick={() => setJwt('')}
                  disabled={saving || !jwt}
                >
                  <FaTimes aria-hidden="true" /> {t('common.actions.clear')}
                </button>
                <button
                  type="submit"
                  className="form-button se-connect-save-btn"
                  disabled={saving || !jwt.trim()}
                >
                  <FaSave aria-hidden="true" />
                  {saving ? t('common.actions.savingEllipsis') : t('common.actions.save')}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </section>
  );
}

export default StreamElementsConnect;
